import type {
  FarmTypeOption,
  IFarmOverviewValidationState,
  IWaterRightFormState,
  OwnershipOption,
} from './farmInformation.types';

export const FARM_OVERVIEW_INITIAL_VALIDATION: IFarmOverviewValidationState = {
  farmName: false,
  farmBackground: false,
  farmDirections: false,
  farmOwnership: false,
  areaLeased: false,
  leaseEndDate: false,
  ownedHectares: false,
  farmType: false,
  areaUnderIrrigation: false,
  areaOfDryLand: false,
  waterRights: false,
  fullTimeFarming: false,
  shortTermGoals: false,
  longTermGoals: false,
  landComposition: false,
  workforceNumber: false,
  workerTrust: false,
  employmentBeforeAfter: false,
};

export const FARM_OVERVIEW_YES_NO_OPTIONS: string[] = ['Yes', 'No'];

export const FARM_OVERVIEW_WATER_SOURCE_OPTIONS: string[] = [
  'Borehole',
  'River',
  'Dam',
];

export const WATER_RIGHTS_YES_NO_OPTIONS = [
  {value: 'no', label: 'No'},
  {value: 'yes', label: 'Yes'},
];

export const WATER_RIGHTS_SOURCE_OPTIONS = [
  {value: 'SPRING', label: 'Spring'},
  {value: 'WELL', label: 'Well'},
  {value: 'MUNICIPAL', label: 'Municipal'},
  {value: 'BOREHOLE', label: 'Borehole'},
  {value: 'RIVER', label: 'River'},
  {value: 'DAM', label: 'Dam'},
];

const WATER_RIGHTS_SOURCE_VALUES = new Set(
  WATER_RIGHTS_SOURCE_OPTIONS.map((option) => option.value),
);

export const WATER_RIGHTS_SCALE_OPTIONS = [
  {value: 'HECTARES', label: 'Hectares'},
  {value: 'CUBIC_METERS', label: 'Cubic meters'},
];

const WATER_RIGHTS_SCALE_VALUES = new Set(
  WATER_RIGHTS_SCALE_OPTIONS.map((option) => option.value),
);

export const EMPTY_WATER_RIGHT_FORM: IWaterRightFormState = {
  waterSource: '',
  householdConsumption: '',
  scheme: '',
  permitNumber: '',
  schedule: '',
  waterScale: '',
  waterQuantity: '',
  controlled: 'no',
  notes: '',
};

export const FARM_OVERVIEW_LEASE_YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];

  for (let year = currentYear; year <= currentYear + 20; year += 1) {
    years.push(year);
  }

  return years;
})();

export function lshFarmOverviewCountWords(value: string): number {
  return value.trim().length === 0
    ? 0
    : value.trim().split(/\s+/).filter(Boolean).length;
}

export function lshResolveOwnershipOption(value?: string): OwnershipOption {
  const text = String(value || '')
    .trim()
    .toUpperCase();

  if (text === 'LEASED') return 'Leased';
  if (text === 'OWNED') return 'Owned';
  if (text === 'BOTH') return 'Both';
  return '';
}

export function lshResolveFarmTypeOption(
  irrigatedHectares?: unknown,
  drylandHectares?: unknown,
): FarmTypeOption {
  const irrigated = Number(irrigatedHectares) > 0;
  const dryland = Number(drylandHectares) > 0;

  if (irrigated && dryland) return 'Both';
  if (irrigated) return 'Irrigated';
  if (dryland) return 'Dryland';
  return '';
}

export function lshNormaliseNumericInput(value: unknown): string {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? String(numeric) : '';
}

export function lshResolveLeaseEndYear(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';

  if (numeric >= 1900 && numeric <= 2500) {
    return String(Math.trunc(numeric));
  }

  if (numeric > 1e9) {
    const year = new Date(numeric * 1000).getUTCFullYear();
    return Number.isFinite(year) ? String(year) : '';
  }

  const modifiedEpoch = new Date('2000-01-01T00:00:00Z').getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  const year = new Date(modifiedEpoch + numeric * msPerDay).getUTCFullYear();
  return Number.isFinite(year) ? String(year) : '';
}

export function lshResolveWaterSources(farm?: {
  borehole?: boolean;
  river?: boolean;
  dam?: boolean;
}): string[] {
  const selected: string[] = [];
  if (farm?.borehole) selected.push('Borehole');
  if (farm?.river) selected.push('River');
  if (farm?.dam) selected.push('Dam');
  return selected;
}

export function lshCsvToArray(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function lshNormalizeEnumToken(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

export function lshParseWaterSourceList(value: string): {
  valid: string[];
  invalid: string[];
} {
  const tokens = lshCsvToArray(value).map(lshNormalizeEnumToken);
  const valid = tokens.filter((token) => WATER_RIGHTS_SOURCE_VALUES.has(token));
  const invalid = tokens.filter(
    (token) => !WATER_RIGHTS_SOURCE_VALUES.has(token),
  );
  return {valid, invalid};
}

export function lshToWaterScaleEnum(value: string): string | null {
  const normalized = lshNormalizeEnumToken(value);
  if (!normalized) return null;
  if (normalized === 'HA' || normalized === 'HECTARE') return 'HECTARES';
  if (normalized === 'CUBIC_METER' || normalized === 'CUBIC_METRE') {
    return 'CUBIC_METERS';
  }
  return WATER_RIGHTS_SCALE_VALUES.has(normalized) ? normalized : null;
}

export function lshSanitizeWaterSourceArray(
  values?: string[] | null,
): string[] {
  return (values || [])
    .map(lshNormalizeEnumToken)
    .filter((token) => WATER_RIGHTS_SOURCE_VALUES.has(token));
}
